import React, { useState } from 'react';
import { Section } from '../../../components/Section';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import useTheme from '../../../hooks/useTheme';
import { megaGreenData } from '../../../data/data';

interface ContactStripProps {
    index?: number;
}

export const ContactStrip: React.FC<ContactStripProps> = ({ index }) => {
    const { theme, breakpoint } = useTheme();
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        serviceNeeded: '',
        message: '',
    });
    const [showSuccess, setShowSuccess] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Contact form submission:', formData);
        setShowSuccess(true);
        setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            serviceNeeded: '',
            message: '',
        });
        setTimeout(() => setShowSuccess(false), 5000);
    };

    return (
        <Section index={index}>
            <div
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                }}
            >
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '16px',
                        textAlign: 'center',
                    }}
                >
                    Tell us what you're planning
                </h2>
                <p
                    style={{
                        fontSize: '16px',
                        color: theme.text.muted,
                        textAlign: 'center',
                        marginBottom: '32px',
                    }}
                >
                    {megaGreenData.contact.pageIntro}
                </p>

                <Card>
                    {showSuccess && (
                        <div
                            style={{
                                padding: '16px',
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                borderRadius: theme.borderRadius.r1,
                                marginBottom: '24px',
                            }}
                        >
                            Thank you! We'll get back to you within {megaGreenData.contact.responseTimePromise}.
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : 'repeat(2, 1fr)',
                                gap: '16px',
                            }}
                        >
                            <div>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Company
                                </label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>

                            <div style={{ gridColumn: breakpoint.xs || breakpoint.sm ? '1' : '1 / -1' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Service Needed
                                </label>
                                <select
                                    name="serviceNeeded"
                                    value={formData.serviceNeeded}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        fontFamily: 'inherit',
                                    }}
                                >
                                    <option value="">Select a service</option>
                                    {megaGreenData.services.services.map((service) => (
                                        <option key={service.id} value={service.id}>
                                            {service.title}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ gridColumn: breakpoint.xs || breakpoint.sm ? '1' : '1 / -1' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    Message *
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: '24px', textAlign: 'center' }}>
                            <Button type="submit" variant="primary" style={{ padding: '12px 32px' }}>
                                Send Message
                            </Button>
                        </div>

                        <p
                            style={{
                                fontSize: '12px',
                                color: theme.text.muted,
                                textAlign: 'center',
                                marginTop: '16px',
                            }}
                        >
                            {megaGreenData.contact.responseTimePromise}
                        </p>
                    </form>
                </Card>
            </div>
        </Section>
    );
};

